/// <amd-module name="Dashboards/WidgetContracts/Internal/ConfigProps" />
import { LoadedEventInfo, LoadingStatus, WidgetTeamContext } from "../WidgetProps";
import { DashboardWidgetProps, WidgetGridSize } from "./DashboardWidgetProps";

// Re-export LoadingStatus & LoadedEventInfo for convenience on Config Implementations
export { LoadingStatus };
export { LoadedEventInfo };

/**
 *  Run-time information for use during Widget Configuration. This is provided by a ConfigHost when the config is rendered.
 */
export interface ConfigParentProps {
    /** Called by Config to notify ConfigHost that loading is done, and the UI is ready for interaction.
     *  Until this happens, host maintains an overlay experience over the widget.
     *
     *  If no information is passed to describe the event, it is assumed to be a successful load.
     *
     * The first non-loading outcome is the only one which matters. Any subsequent notifications are ignored.
     */
    onConfigLoaded(loadedEventInfo?: LoadedEventInfo): void;

    /** Callback for parent to re-render a preview. */
    onUpdate(widget: DashboardWidgetProps): void;

    /** Callback to parent to indicate if dashboard state is ready for a save.  */
    onValidationResponse(widget: DashboardWidgetProps, isValidToSave: boolean): void;
}

/***
 * Restrictions imposed by developer about the configuraiton experience.
 */
export interface ContributionConfigConstraints {
    /**
     * Indicates if platform should provide a configuration affordance for name property.
     */
    isNameConfigurable: boolean;

    /** Sizes supported by the widget.
     * The first is used as a default in Configuration Experiences.
     */
    supportedSizes: WidgetGridSize[];
}

/** Props going from Config Host to Widget Config UI implementations. */
export interface ConfigProps {
    config: DashboardWidgetProps;
    contributionConstraints: ContributionConfigConstraints;
    parentProps: ConfigParentProps;

    /**
     * Describes the last time a request for save validation was sent down to a config.
     *
     * Config authors should use previous props in `componentDidUpdate` and check whether the
     * this value in the current props differs from the previous value. When they differ,
     * that represents a request for validation from the parent, and the author should
     * call `onValidationResponse`.
     */
    saveValidationTimeStamp?: Date;

    /** Describes default team a widget should be operating under. */
    teamContext: WidgetTeamContext;
}
